import { Focus, MousePointer2, Pencil, Redo2, RotateCcw, Save, Undo2 } from 'lucide-react';
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FlatfinderPerspective,
  FlatfinderPoint,
  FlatfinderRegion,
} from 'realitiq-utils/src/flatfinder';
import { isValidFlatfinderPolygon } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import { env } from '@/constants/env';
import { AreaColorPicker } from './area-color-picker';
import { FlatfinderToolbarButton } from './flatfinder-toolbar-button';
import { useAreaClipboard } from './use-area-clipboard';
import {
  useDrawingChangeNotification,
  useDrawingKeyboardShortcuts,
  useHoveredEstateSynchronization,
  usePolygonEditorScreenScale,
  usePolygonEditorWheelZoom,
  useSelectedAreaEdgeInvalidation,
  useSelectedVertexReset,
} from './polygon-editor-effects';
import {
  type AreaDrag,
  type AreaEdgeCandidate,
  calculatePinchZoomedView,
  calculatePointBetweenPointers,
  calculatePointDistance,
  convertClientPointToElementPoint,
  convertClientPointToNormalizedImagePoint,
  findNearestSelectedAreaEdge,
  isPointInsideNormalizedImage,
  moveAreaVertex,
  moveAreaWithinImage,
  type PendingAreaEdge,
  type PinchGesture,
  type PolygonEditorView,
} from './polygon-editor-utils';

export function PolygonEditor({
  perspective,
  selectedId,
  hoveredEstateId,
  onHoverEstate,
  assigning = false,
  onAssignArea,
  onCancelAssignment,
  onSelect,
  onChange,
  onError,
  disabled = false,
  perspectiveControls,
  onDrawingChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSave,
  saving,
}: {
  perspective: FlatfinderPerspective;
  selectedId: string | null;
  hoveredEstateId?: string | null;
  onHoverEstate?: (id: string | null) => void;
  assigning?: boolean;
  onAssignArea?: (id: string) => void;
  onCancelAssignment?: () => void;
  onSelect: (id: string | null) => void;
  onChange: (regions: FlatfinderRegion[]) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  perspectiveControls?: ReactNode;
  onDrawingChange?: (dirty: boolean) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onSave: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const svg = useRef<SVGSVGElement>(null);
  const transformedImageGroupRef = useRef<SVGGElement>(null);
  const activePointerPositionsRef = useRef(new Map<number, FlatfinderPoint>());
  const pinchGestureRef = useRef<PinchGesture | null>(null);
  const areaDragRef = useRef<AreaDrag | null>(null);
  const viewportPanRef = useRef<{ start: FlatfinderPoint; view: PolygonEditorView } | null>(null);
  const pointerDownPositionRef = useRef<FlatfinderPoint | null>(null);
  const shouldSuppressClickRef = useRef(false);
  const [view, setView] = useState<PolygonEditorView>({ x: 0, y: 0, zoom: 1 });
  const [drawingColor, setDrawingColor] = useState('#2563eb');
  const [mode, setMode] = useState<'select' | 'draw'>('select');
  const [hoveredRegionId, setHoveredRegionId] = useState<string | null>(null);
  const [draftAreaPoints, setDraftAreaPoints] = useState<FlatfinderPoint[]>([]);
  const [redoableAreaPoints, setRedoableAreaPoints] = useState<FlatfinderPoint[]>([]);
  const [areaPointPreview, setAreaPointPreview] = useState<FlatfinderPoint[] | null>(null);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState<number | null>(null);
  const [drawingCursorPoint, setDrawingCursorPoint] = useState<FlatfinderPoint | null>(null);
  const [edge, setHoveredAreaEdge] = useState<AreaEdgeCandidate | null>(null);
  const pendingAreaEdgeRef = useRef<PendingAreaEdge | null>(null);
  const selectedArea = perspective.regions.find((region) => region.id === selectedId);
  const screenToImageScale = usePolygonEditorScreenScale(
    svg,
    perspective.width,
    perspective.height
  );
  const cancelDrawing = useCallback(() => {
    setDraftAreaPoints([]);
    setRedoableAreaPoints([]);
    setDrawingCursorPoint(null);
    setMode('select');
  }, []);
  const clearHoveredEdge = useCallback(() => setHoveredAreaEdge(null), []);

  useHoveredEstateSynchronization({
    regions: perspective.regions,
    hoveredRegionId,
    selectionEnabled: mode === 'select' && !disabled,
    onHoverEstate,
  });
  useDrawingChangeNotification(draftAreaPoints.length, onDrawingChange);
  useSelectedAreaEdgeInvalidation({
    regions: perspective.regions,
    selectedAreaId: selectedId,
    pendingEdge: pendingAreaEdgeRef,
    setHoveredEdge: setHoveredAreaEdge,
  });
  useSelectedVertexReset(selectedId, setSelectedVertexIndex);
  useAreaClipboard({
    enabled: mode === 'select' && !disabled && !assigning && areaPointPreview === null,
    selectedArea,
    regions: perspective.regions,
    onChange,
    onSelect,
  });
  useDrawingKeyboardShortcuts({
    drawing: draftAreaPoints,
    undonePoints: redoableAreaPoints,
    drawingEnabled: mode === 'draw' && !disabled,
    setDrawing: setDraftAreaPoints,
    setUndonePoints: setRedoableAreaPoints,
    cancelDrawing,
  });
  usePolygonEditorWheelZoom({ svg, setView, clearHoveredEdge });

  function getNormalizedImagePoint(clientPoint: FlatfinderPoint) {
    return convertClientPointToNormalizedImagePoint(
      transformedImageGroupRef.current,
      clientPoint,
      perspective.width,
      perspective.height
    );
  }

  function getSvgViewportPoint(clientPoint: FlatfinderPoint) {
    return convertClientPointToElementPoint(svg.current, clientPoint);
  }

  function commitAreaPointChanges(region: FlatfinderRegion, points: FlatfinderPoint[]) {
    if (!isValidFlatfinderPolygon(points)) {
      onError(t('flatfinderControlInvalidArea'));
      return;
    }
    onChange(
      perspective.regions.map((item) => (item.id === region.id ? { ...item, points } : item))
    );
  }

  function finishAreaDrawing() {
    if (!isValidFlatfinderPolygon(draftAreaPoints)) {
      onError(t('flatfinderControlInvalidDrawing'));
      return;
    }
    const region: FlatfinderRegion = {
      id: crypto.randomUUID(),
      estateId: null,
      points: draftAreaPoints,
      color: drawingColor,
      sortOrder: perspective.regions.length,
    };
    onChange([...perspective.regions, region]);
    onSelect(region.id);
    setDraftAreaPoints([]);
    setRedoableAreaPoints([]);
    setDrawingCursorPoint(null);
    setMode('select');
  }

  function findNearestSelectedAreaEdgeAtClientPoint(clientPoint: FlatfinderPoint) {
    return findNearestSelectedAreaEdge({
      imageElement: transformedImageGroupRef.current,
      clientPoint,
      selectedArea,
      imageWidth: perspective.width,
      imageHeight: perspective.height,
    });
  }

  function handlePointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    if (assigning || disabled || event.button > 0) return;
    const client = { x: event.clientX, y: event.clientY };
    activePointerPositionsRef.current.set(event.pointerId, client);
    event.currentTarget.setPointerCapture(event.pointerId);
    if (activePointerPositionsRef.current.size === 2) {
      const [a, b] = [...activePointerPositionsRef.current.values()];
      const center = calculatePointBetweenPointers(a, b);
      pinchGestureRef.current = {
        distance: calculatePointDistance(a, b),
        center: getSvgViewportPoint(center),
        view,
      };
      pendingAreaEdgeRef.current = null;
      setHoveredAreaEdge(null);
      areaDragRef.current = null;
      setAreaPointPreview(null);
      viewportPanRef.current = null;
      shouldSuppressClickRef.current = true;
      return;
    }
    pointerDownPositionRef.current = client;
    shouldSuppressClickRef.current = false;
    setDrawingCursorPoint(null);
    setHoveredAreaEdge(null);
    if (mode === 'draw') {
      viewportPanRef.current = { start: getSvgViewportPoint(client), view };
      return;
    }
    const matrix = transformedImageGroupRef.current?.getScreenCTM();
    const marker =
      edge && matrix
        ? new DOMPoint(
            edge.point.x * perspective.width,
            edge.point.y * perspective.height
          ).matrixTransform(matrix)
        : null;
    if (
      edge &&
      marker &&
      Math.hypot(client.x - marker.x, client.y - marker.y) <=
        (event.pointerType === 'touch' ? 30 : 24)
    ) {
      pendingAreaEdgeRef.current = { edge, insert: true };
      viewportPanRef.current = { start: getSvgViewportPoint(client), view };
      return;
    }
    if (event.pointerType === 'touch') {
      const nearest = findNearestSelectedAreaEdgeAtClientPoint(client);
      if (nearest) {
        pendingAreaEdgeRef.current = { edge: nearest, insert: false };
        viewportPanRef.current = { start: getSvgViewportPoint(client), view };
        return;
      }
    }
    const target = event.target as Element;
    const regionId = target.closest('[data-region]')?.getAttribute('data-region');
    const region = perspective.regions.find((item) => item.id === regionId);
    onSelect(region?.id ?? null);
    if (region) {
      const vertexValue = target.getAttribute('data-vertex');
      const index = vertexValue === null ? null : Number(vertexValue);
      setSelectedVertexIndex(index);
      areaDragRef.current = {
        region,
        vertex: index,
        start: getNormalizedImagePoint(client),
        points: region.points,
      };
    } else {
      viewportPanRef.current = { start: getSvgViewportPoint(client), view };
    }
  }

  function handlePointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    const target = event.target as Element;
    setHoveredRegionId(
      (assigning || mode === 'select') && !disabled
        ? (target.closest('[data-region]')?.getAttribute('data-region') ?? null)
        : null
    );
    if (assigning) return;
    if (!activePointerPositionsRef.current.has(event.pointerId)) {
      if (disabled) return;
      const client = { x: event.clientX, y: event.clientY };
      if (mode === 'select') {
        const nearest = findNearestSelectedAreaEdgeAtClientPoint(client);
        setHoveredAreaEdge(nearest);
        setDrawingCursorPoint(null);
      } else {
        const point = getNormalizedImagePoint(client);
        setDrawingCursorPoint(isPointInsideNormalizedImage(point) ? point : null);
      }
      return;
    }
    const client = { x: event.clientX, y: event.clientY };
    activePointerPositionsRef.current.set(event.pointerId, client);
    if (pinchGestureRef.current && activePointerPositionsRef.current.size === 2) {
      const [a, b] = [...activePointerPositionsRef.current.values()];
      const start = pinchGestureRef.current;
      const requestedZoom =
        (start.view.zoom * calculatePointDistance(a, b)) / Math.max(start.distance, 1);
      const center = getSvgViewportPoint(calculatePointBetweenPointers(a, b));
      setView(calculatePinchZoomedView(start.view, start.center, center, requestedZoom));
      return;
    }
    if (
      viewportPanRef.current &&
      pointerDownPositionRef.current &&
      Math.hypot(
        client.x - pointerDownPositionRef.current.x,
        client.y - pointerDownPositionRef.current.y
      ) >= 5
    ) {
      shouldSuppressClickRef.current = true;
      pendingAreaEdgeRef.current = null;
      const p = getSvgViewportPoint(client);
      setView({
        ...viewportPanRef.current.view,
        x: viewportPanRef.current.view.x + p.x - viewportPanRef.current.start.x,
        y: viewportPanRef.current.view.y + p.y - viewportPanRef.current.start.y,
      });
    }
    if (areaDragRef.current) {
      const current = areaDragRef.current;
      const p = getNormalizedImagePoint(client);
      if (current.vertex !== null) {
        current.points = moveAreaVertex(current.region, current.vertex, p);
      } else {
        current.points = moveAreaWithinImage(current.region, current.start, p);
      }
      setAreaPointPreview(current.points);
    }
  }

  function handlePointerUp(event: ReactPointerEvent<SVGSVGElement>, cancelled = false) {
    activePointerPositionsRef.current.delete(event.pointerId);
    if (pinchGestureRef.current) {
      pinchGestureRef.current = null;
      const remaining = [...activePointerPositionsRef.current.values()][0];
      pointerDownPositionRef.current = remaining ?? null;
      viewportPanRef.current = remaining ? { start: getSvgViewportPoint(remaining), view } : null;
      return;
    }
    const candidate = pendingAreaEdgeRef.current;
    pendingAreaEdgeRef.current = null;
    if (
      candidate &&
      !cancelled &&
      !shouldSuppressClickRef.current &&
      pointerDownPositionRef.current &&
      Math.hypot(
        event.clientX - pointerDownPositionRef.current.x,
        event.clientY - pointerDownPositionRef.current.y
      ) < 5
    ) {
      if (candidate.insert) {
        const points = [...candidate.edge.region.points];
        points.splice(candidate.edge.index + 1, 0, candidate.edge.point);
        commitAreaPointChanges(candidate.edge.region, points);
        onSelect(candidate.edge.region.id);
        setSelectedVertexIndex(candidate.edge.index + 1);
      } else {
        setHoveredAreaEdge({ ...candidate.edge, hovered: true });
      }
    }
    if (
      areaDragRef.current &&
      !cancelled &&
      areaDragRef.current.points !== areaDragRef.current.region.points
    )
      commitAreaPointChanges(areaDragRef.current.region, areaDragRef.current.points);
    if (
      !cancelled &&
      mode === 'draw' &&
      !shouldSuppressClickRef.current &&
      pointerDownPositionRef.current &&
      Math.hypot(
        event.clientX - pointerDownPositionRef.current.x,
        event.clientY - pointerDownPositionRef.current.y
      ) < 8
    ) {
      const point = getNormalizedImagePoint({ x: event.clientX, y: event.clientY });
      if (isPointInsideNormalizedImage(point)) {
        const first = draftAreaPoints[0];
        const firstScreen = first && transformedImageGroupRef.current?.getScreenCTM();
        const firstPoint =
          first && firstScreen
            ? new DOMPoint(
                first.x * perspective.width,
                first.y * perspective.height
              ).matrixTransform(firstScreen)
            : null;
        if (
          draftAreaPoints.length >= 3 &&
          firstPoint &&
          Math.hypot(firstPoint.x - event.clientX, firstPoint.y - event.clientY) < 18
        )
          finishAreaDrawing();
        else {
          setDraftAreaPoints((points) => [...points, point]);
          setRedoableAreaPoints([]);
        }
      }
    }
    areaDragRef.current = null;
    viewportPanRef.current = null;
    pointerDownPositionRef.current = null;
    if (!cancelled && mode === 'draw' && !shouldSuppressClickRef.current) {
      const point = getNormalizedImagePoint({ x: event.clientX, y: event.clientY });
      setDrawingCursorPoint(isPointInsideNormalizedImage(point) ? point : null);
    }
    setAreaPointPreview(null);
  }

  function undoLatestEditorChange() {
    const point = draftAreaPoints.at(-1);
    if (mode === 'draw' && point) {
      setDraftAreaPoints(draftAreaPoints.slice(0, -1));
      setRedoableAreaPoints([point, ...redoableAreaPoints]);
    } else onUndo();
  }

  function redoLatestEditorChange() {
    const point = redoableAreaPoints[0];
    if (mode === 'draw' && point) {
      setDraftAreaPoints([...draftAreaPoints, point]);
      setRedoableAreaPoints(redoableAreaPoints.slice(1));
    } else onRedo();
  }

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border bg-background p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-full shrink-0">{perspectiveControls}</div>
        <div className="flex max-w-full flex-wrap items-center justify-center gap-1 rounded-sm border p-1">
          <FlatfinderToolbarButton
            icon={<MousePointer2 />}
            label={t('select')}
            variant={mode === 'select' ? 'outline' : 'ghost'}
            disabled={disabled}
            aria-pressed={mode === 'select'}
            onClick={cancelDrawing}
          />
          <FlatfinderToolbarButton
            icon={<Pencil />}
            label={t('draw')}
            variant={mode === 'draw' ? 'outline' : 'ghost'}
            disabled={disabled}
            aria-pressed={mode === 'draw'}
            onClick={() => {
              setMode('draw');
              setHoveredAreaEdge(null);
              setDrawingCursorPoint(null);
            }}
          />
          <AreaColorPicker
            color={mode === 'select' && selectedArea ? selectedArea.color : drawingColor}
            disabled={disabled || assigning}
            onChange={(color) => {
              setDrawingColor(color);
              if (mode === 'select' && selectedArea)
                onChange(
                  perspective.regions.map((region) =>
                    region.id === selectedArea.id ? { ...region, color } : region
                  )
                );
            }}
          />
          <FlatfinderToolbarButton
            icon={<RotateCcw />}
            label={t('reset')}
            variant="ghost"
            disabled={disabled || (!perspective.regions.length && !draftAreaPoints.length)}
            onClick={() => {
              onChange([]);
              onSelect(null);
              setDraftAreaPoints([]);
              setRedoableAreaPoints([]);
              setHoveredAreaEdge(null);
              setDrawingCursorPoint(null);
            }}
          />
          <FlatfinderToolbarButton
            icon={<Focus />}
            label={t('center')}
            variant="ghost"
            onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
          />
          <div className="mx-1 h-9 border-l" />
          <FlatfinderToolbarButton
            icon={<Undo2 />}
            variant="ghost"
            aria-label={t('undoLatestEditorChange')}
            title={t('undoLatestEditorChange')}
            disabled={disabled || !(draftAreaPoints.length || canUndo)}
            onClick={undoLatestEditorChange}
          />
          <FlatfinderToolbarButton
            icon={<Redo2 />}
            variant="ghost"
            aria-label={t('redoLatestEditorChange')}
            title={t('redoLatestEditorChange')}
            disabled={disabled || !(redoableAreaPoints.length || canRedo)}
            onClick={redoLatestEditorChange}
          />
        </div>
        <FlatfinderToolbarButton
          icon={<Save />}
          label={t('save')}
          className="ml-auto"
          disabled={disabled || draftAreaPoints.length > 0}
          loading={saving}
          onClick={onSave}
        />
      </div>
      <div className="relative overflow-hidden rounded-sm border bg-muted/20">
        <svg
          ref={svg}
          data-assignment-surface
          role="img"
          aria-label={t('flatfinderControlEditor')}
          viewBox={`0 0 ${perspective.width} ${perspective.height}`}
          className="h-[55dvh] min-h-72 w-full touch-none select-none overflow-hidden lg:h-[65dvh]"
          onPointerLeave={(event) => {
            setHoveredRegionId(null);
            if (event.pointerType !== 'touch') {
              setHoveredAreaEdge(null);
              setDrawingCursorPoint(null);
            }
          }}
          onPointerEnter={(event) => {
            if (!assigning && event.pointerType === 'mouse' && mode === 'select' && !disabled)
              setHoveredAreaEdge(
                findNearestSelectedAreaEdgeAtClientPoint({
                  x: event.clientX,
                  y: event.clientY,
                })
              );
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={(event) => {
            if (!assigning) handlePointerUp(event);
          }}
          onClick={(event) => {
            if (!assigning || disabled) return;
            const target = event.target as Element;
            const id = target
              .closest('[data-assignment-area]')
              ?.getAttribute('data-assignment-area');
            if (id) onAssignArea?.(id);
          }}
          onKeyDown={(event) => {
            if (!assigning || disabled || !['Enter', ' '].includes(event.key)) return;
            const id = (event.target as Element).getAttribute('data-assignment-area');
            if (id) {
              event.preventDefault();
              onAssignArea?.(id);
            }
          }}
          onPointerCancel={(event) => handlePointerUp(event, true)}
        >
          <g
            ref={transformedImageGroupRef}
            transform={`translate(${view.x} ${view.y}) scale(${view.zoom})`}
          >
            <image
              href={`${env.API_URL}/images/${perspective.mediaId}`}
              width={perspective.width}
              height={perspective.height}
            />
            {perspective.regions
              .filter((region) => !assigning || !region.estateId)
              .map((region) => (
                <polygon
                  key={region.id}
                  data-region={region.id}
                  data-assignment-area={assigning ? region.id : undefined}
                  tabIndex={assigning ? 0 : undefined}
                  role={assigning ? 'button' : undefined}
                  aria-label={assigning ? t('flatfinderControlAssignArea') : undefined}
                  points={(region.id === selectedId && areaPointPreview
                    ? areaPointPreview
                    : region.points
                  )
                    .map(
                      (point) => `${point.x * perspective.width},${point.y * perspective.height}`
                    )
                    .join(' ')}
                  fill={region.color}
                  fillOpacity={0.35}
                  style={{
                    filter:
                      (assigning || mode === 'select') &&
                      (region.id === hoveredRegionId ||
                        (hoveredEstateId && region.estateId === hoveredEstateId))
                        ? 'brightness(1.3)'
                        : undefined,
                  }}
                  stroke={region.color}
                  strokeDasharray={region.id === selectedId ? '6 3' : undefined}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            {!assigning &&
              selectedArea &&
              mode === 'select' &&
              (areaPointPreview ?? selectedArea.points).map((point, i) => (
                <g key={`${selectedArea.id}-${i}`}>
                  <circle
                    data-region={selectedArea.id}
                    data-vertex={i}
                    cx={point.x * perspective.width}
                    cy={point.y * perspective.height}
                    r={18 / screenToImageScale / view.zoom}
                    fill="transparent"
                  />
                  <circle
                    pointerEvents="none"
                    cx={point.x * perspective.width}
                    cy={point.y * perspective.height}
                    r={6 / screenToImageScale / view.zoom}
                    fill={selectedVertexIndex === i ? '#f59e0b' : '#fff'}
                    stroke={drawingColor}
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              ))}
            {!assigning && edge?.region.id === selectedId && mode === 'select' && !disabled && (
              <g
                pointerEvents="none"
                transform={`translate(${edge.point.x * perspective.width} ${edge.point.y * perspective.height}) scale(${1 / screenToImageScale / view.zoom})`}
                className="cursor-copy"
              >
                <circle
                  r={edge.hovered ? 13 : 10}
                  fill="white"
                  stroke="currentColor"
                  strokeWidth={1.5}
                />
                <path
                  d="M -4 0 H 4 M 0 -4 V 4"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  pointerEvents="none"
                />
              </g>
            )}
            {!assigning && draftAreaPoints.length > 0 && (
              <polyline
                points={draftAreaPoints
                  .map((point) => `${point.x * perspective.width},${point.y * perspective.height}`)
                  .join(' ')}
                fill={`${drawingColor}22`}
                stroke={drawingColor}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            )}
            {drawingCursorPoint && draftAreaPoints.length > 0 && mode === 'draw' && !disabled && (
              <line
                x1={draftAreaPoints[draftAreaPoints.length - 1].x * perspective.width}
                y1={draftAreaPoints[draftAreaPoints.length - 1].y * perspective.height}
                x2={drawingCursorPoint.x * perspective.width}
                y2={drawingCursorPoint.y * perspective.height}
                stroke={drawingColor}
                strokeWidth={2}
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            )}
            {draftAreaPoints.map((point, i) => (
              <circle
                key={`${point.x}-${point.y}-${i}`}
                cx={point.x * perspective.width}
                cy={point.y * perspective.height}
                r={(Math.max(perspective.width, perspective.height) * 0.007) / view.zoom}
                fill={i === 0 ? '#16a34a' : '#2563eb'}
              />
            ))}
            {!assigning && drawingCursorPoint && mode === 'draw' && !disabled && (
              <g
                pointerEvents="none"
                transform={`translate(${drawingCursorPoint.x * perspective.width} ${drawingCursorPoint.y * perspective.height}) scale(${1 / screenToImageScale / view.zoom})`}
              >
                <circle
                  r="10"
                  fill="white"
                  fillOpacity="0.9"
                  stroke={drawingColor}
                  strokeWidth="1.5"
                />
                <path d="M -4 0 H 4 M 0 -4 V 4" stroke={drawingColor} strokeWidth="1.5" />
              </g>
            )}
          </g>
        </svg>
        {assigning ? (
          <Button
            className="absolute left-1/2 top-3 -translate-x-1/2 bg-white"
            variant="outline"
            onClick={onCancelAssignment}
          >
            {t('cancel')}
          </Button>
        ) : mode === 'draw' ? (
          <div className="absolute right-3 top-3 flex items-center gap-2 rounded-sm border bg-background p-2 shadow-sm">
            <span className="text-xs">{t('clickToPlacePoints')}</span>
            <Button
              size="sm"
              disabled={disabled || draftAreaPoints.length < 3}
              onClick={finishAreaDrawing}
            >
              {t('finishArea')}
            </Button>
          </div>
        ) : selectedArea ? (
          <div className="absolute right-3 top-3 flex flex-wrap justify-end gap-2 rounded-sm border bg-background p-2 shadow-sm">
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || selectedVertexIndex === null || selectedArea.points.length <= 3}
              onClick={() => {
                commitAreaPointChanges(
                  selectedArea,
                  selectedArea.points.filter((_, i) => i !== selectedVertexIndex)
                );
                setSelectedVertexIndex(null);
              }}
            >
              {t('removeCorner')}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={disabled}
              onClick={() => {
                onChange(perspective.regions.filter((region) => region.id !== selectedArea.id));
                onSelect(null);
                setHoveredAreaEdge(null);
              }}
            >
              {t('deleteArea')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
